import { useState } from 'react';
import { getModuleOption, plansIncludingLabels, planContents, PLANS, type ModuleKey } from '../../lib/plans';
import { requestOption } from '../../lib/optionRequestService';

/**
 * Vitrine STANDARDISÉE d'un module optionnel verrouillé (même format partout).
 * Affichée à la place du module quand l'option n'est pas active : accroche,
 * KPIs colorés (« pourquoi souscrire »), bénéfices, prix à la carte + formules,
 * et un CTA d'activation. Le clic « Activer » envoie une DEMANDE à Lil & Co
 * (notification email contact@yumlink.io) ; l'activation réelle se fait en super admin.
 */
export default function OptionShowcase({
  moduleKey,
  tenant,
  onSubscribe,
}: {
  moduleKey: ModuleKey;
  /** Resto courant (pour notifier Lil & Co de la demande d'activation). */
  tenant?: { name: string; slug: string };
  /** Optionnel : action de souscription (sinon envoie une demande + confirmation). */
  onSubscribe?: () => void;
}) {
  const opt = getModuleOption(moduleKey);
  const [requested, setRequested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!opt) return null;
  const formules = plansIncludingLabels(moduleKey);

  const subscribe = async () => {
    if (onSubscribe) {
      onSubscribe();
      setRequested(true);
      return;
    }
    setErr(null);
    if (tenant) {
      setBusy(true);
      const res = await requestOption({ tenantName: tenant.name, tenantSlug: tenant.slug, moduleKey, moduleTitle: opt.title });
      setBusy(false);
      if (!res.ok) {
        setErr(res.error ?? 'Demande impossible. Réessaie ou contacte Lil & Co.');
        return;
      }
    }
    setRequested(true);
  };

  return (
    <div className="mx-auto max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      {/* En-tête de marque */}
      <div
        className="px-6 py-7 text-white"
        style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-dark))' }}
      >
        <div className="flex items-center gap-3">
          <span className="text-4xl">{opt.emoji}</span>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide opacity-80">Option YumLink</div>
            <h2 className="text-2xl font-extrabold leading-tight">{opt.title}</h2>
          </div>
        </div>
        <p className="mt-2 text-sm opacity-95">{opt.tagline}</p>
      </div>

      <div className="p-6">
        {/* KPIs attractifs */}
        <div className="grid grid-cols-2 gap-3">
          {opt.kpis.map((k) => (
            <div key={k.label} className="rounded-xl bg-slate-50 p-4 text-center">
              <div className={`text-3xl font-extrabold ${k.color}`}>{k.value}</div>
              <div className="mt-1 text-xs text-slate-500">{k.label}</div>
            </div>
          ))}
        </div>

        {/* Bénéfices */}
        <ul className="mt-5 space-y-2">
          {opt.benefits.map((b) => (
            <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
              <span className="mt-0.5 text-emerald-500">✓</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>

        {/* Prix + formules */}
        <div className="mt-6 flex flex-wrap items-end justify-between gap-3 rounded-xl border border-slate-200 p-4">
          <div>
            <div className="text-xs text-slate-500">Option seule</div>
            <div className="text-2xl font-extrabold">{opt.soloPriceLabel}</div>
            {formules.length > 0 && (
              <div className="mt-1 text-xs text-slate-500">
                Incluse dans&nbsp;
                {formules.map((f) => (
                  <span key={f} className="mr-1 inline-block rounded-md bg-brand-light px-1.5 py-0.5 font-semibold text-brand-primary">
                    {f}
                  </span>
                ))}
              </div>
            )}
          </div>
          {requested ? (
            <div className="rounded-lg bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
              ✅ Demande envoyée — Lil &amp; Co vous recontacte pour activer (sous 24 h).
            </div>
          ) : (
            <button
              onClick={subscribe}
              disabled={busy}
              className="rounded-xl px-5 py-3 text-sm font-extrabold text-white shadow transition hover:opacity-90 disabled:opacity-50"
              style={{ background: 'var(--brand-primary)' }}
            >
              {busy ? 'Envoi…' : '🔓 Demander l’activation'}
            </button>
          )}
        </div>
        {err && <p className="mt-2 text-center text-sm text-red-600">{err}</p>}

        {/* Les 3 formules YumLink */}
        <div className="mt-6">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Nos formules</div>
          <div className="grid gap-3 sm:grid-cols-3">
            {PLANS.map((p) => {
              const included = p.modules.includes(opt.key);
              return (
                <div
                  key={p.key}
                  className={
                    'rounded-xl border p-3 ' +
                    (included ? 'border-brand-primary bg-brand-light' : 'border-slate-200 bg-white')
                  }
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold">{p.label}</span>
                    {p.highlight && (
                      <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">★ populaire</span>
                    )}
                  </div>
                  <div className="text-lg font-extrabold">{p.priceLabel}</div>
                  {included ? (
                    <div className="mt-1 text-xs font-semibold text-brand-primary">✓ cette option incluse</div>
                  ) : (
                    <div className="mt-1 text-xs text-slate-400">option non incluse</div>
                  )}
                  {/* Rappel : ce que contient cette formule */}
                  <ul className="mt-2 space-y-1 border-t border-slate-200/70 pt-2">
                    {planContents(p.key).map((c, i) => {
                      const isCurrent = c.key === opt.key;
                      return (
                        <li
                          key={c.key ?? `base-${i}`}
                          className={
                            'flex items-start gap-1.5 text-[11px] leading-tight ' +
                            (isCurrent ? 'font-bold text-brand-primary' : 'text-slate-600')
                          }
                        >
                          <span aria-hidden>{c.emoji}</span>
                          <span>{c.label}{isCurrent ? ' ←' : ''}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        <p className="mt-3 text-center text-xs text-slate-400">
          Activation gérée par Lil &amp; Co · sans engagement · résiliable à tout moment.
        </p>
      </div>
    </div>
  );
}
